import {
  ChoiceItem,
  DisplayLogicExpression,
  QualtricsDisplayGroup,
  QualtricsForceResponse,
  QualtricsSurvey,
  QualtricsSurveySubmit,
  QuestionLanguage,
  SurveyQuestion,
} from '../models/qualtrics-feedback-form.model';
import {
  QUALTRICS_HORIZONTAL_SELECTORS,
  QUALTRICS_MULTI_SELECT_SELECTORS,
  QUALTRICS_QUESTION_TYPES,
} from './qualtrics-feedback.constants';

function getChoiceSeparatorIndex(display: string): number {
  const dashSeparatorIndex = display.indexOf(' - ');

  if (dashSeparatorIndex > -1) {
    return dashSeparatorIndex;
  }

  return display.indexOf(' | ');
}

function getLocalizedLanguageContent(
  question: SurveyQuestion,
  portalLanguage: string,
): QuestionLanguage[string] | undefined {
  if (!question.Language) {
    return undefined;
  }

  return question.Language[portalLanguage];
}

export function getQuestionText(
  question: SurveyQuestion,
  portalLanguage: string,
): string {
  const localizedQuestion = getLocalizedLanguageContent(
    question,
    portalLanguage,
  )?.['QuestionText'];

  return localizedQuestion ?? question.QuestionText ?? '';
}

function getLocalizedChoices(
  question: SurveyQuestion,
  portalLanguage: string,
): Record<string, ChoiceItem> | undefined {
  const localizedChoices = getLocalizedLanguageContent(
    question,
    portalLanguage,
  )?.['Choices'];

  if (localizedChoices) {
    return localizedChoices;
  }

  if (!Array.isArray(question.Choices) || !question.Choices.length) {
    return undefined;
  }

  const choiceOrder = question.ChoiceOrder?.length
    ? question.ChoiceOrder.map((choiceId: number) => String(choiceId))
    : question.Choices.map((_, index) => String(index + 1));

  return choiceOrder.reduce<Record<string, ChoiceItem>>(
    (accumulator, choiceId: string, index: number) => {
      const choice = question.Choices[index];
      accumulator[choiceId] = {
        display: choice?.display ?? (choice as { Display?: string }).Display,
      };

      return accumulator;
    },
    {},
  );
}

export function getQuestionChoices(
  question: SurveyQuestion,
  portalLanguage: string,
): Array<{ id: string; display: string }> {
  const choices = getLocalizedChoices(question, portalLanguage);

  if (!choices) {
    return [];
  }

  if (question.ChoiceOrder?.length) {
    return question.ChoiceOrder.map((choiceId: number) => ({
      id: String(choiceId),
      display:
        choices[String(choiceId)]?.display ??
        choices[String(choiceId)]?.['Display'] ??
        String(choiceId),
    }));
  }

  return Object.keys(choices).map((choiceId) => ({
    id: choiceId,
    display:
      choices[choiceId]?.display ?? choices[choiceId]?.['Display'] ?? choiceId,
  }));
}

export function getChoiceOption(display: string): string {
  const separatorIndex = getChoiceSeparatorIndex(display);

  if (separatorIndex > -1) {
    return display.slice(0, separatorIndex).trim();
  }

  return display;
}

export function getChoiceLabel(display: string): string {
  const separatorIndex = getChoiceSeparatorIndex(display);

  if (separatorIndex > -1) {
    return display.slice(separatorIndex + 3).trim();
  }

  return '';
}

export function isHorizontalSelector(
  selector: SurveyQuestion['Selector'],
): boolean {
  return QUALTRICS_HORIZONTAL_SELECTORS.includes(
    selector as (typeof QUALTRICS_HORIZONTAL_SELECTORS)[number],
  );
}

export function isMultiSelectSelector(
  selector: SurveyQuestion['Selector'],
): boolean {
  return QUALTRICS_MULTI_SELECT_SELECTORS.includes(
    selector as (typeof QUALTRICS_MULTI_SELECT_SELECTORS)[number],
  );
}

export function normalizePortalLanguageCode(language: string): string {
  return language.toUpperCase();
}

function evaluateExpression(
  expression: DisplayLogicExpression,
  answers: Record<string, string | string[]>,
): boolean {
  if (expression.Operator !== 'Selected') {
    return true;
  }

  const locator = expression.LeftOperand ?? expression.ChoiceLocator;

  if (!locator) {
    return true;
  }

  const matches = locator.match(/q:\/\/(QID\d+)\/SelectableChoice\/(\d+)/);

  if (!matches) {
    return true;
  }

  const [, questionId, choiceId] = matches;
  const answer = answers[questionId];

  if (Array.isArray(answer)) {
    return answer.includes(choiceId);
  }

  return answer === choiceId;
}

function evaluateLogicGroup(
  group: QualtricsDisplayGroup,
  answers: Record<string, string | string[]>,
): boolean {
  const expressionKeys = Object.keys(group)
    .filter((key) => key !== 'Type')
    .sort((a, b) => Number(a) - Number(b));

  let result: boolean | null = null;

  expressionKeys.forEach((expressionKey) => {
    const expression = group[expressionKey] as
      | DisplayLogicExpression
      | undefined;

    if (!expression) {
      return;
    }

    const expressionResult = evaluateExpression(expression, answers);

    if (result === null) {
      result = expressionResult;

      return;
    }

    const conjunction = expression.Conjuction?.toLowerCase() ?? 'and';
    result =
      conjunction === 'or'
        ? result || expressionResult
        : result && expressionResult;
  });

  return result ?? true;
}

function evaluateDisplayLogic(
  logic: DisplayLogicExpression,
  answers: Record<string, string | string[]>,
): boolean {
  const groups = Object.keys(logic)
    .filter((key) => key !== 'Type' && key !== 'inPage')
    .sort((a, b) => Number(a) - Number(b));

  if (!groups.length) {
    return true;
  }

  return groups.every((groupKey) => {
    const group = logic[groupKey] as QualtricsDisplayGroup | undefined;

    return group && typeof group === 'object'
      ? evaluateLogicGroup(group, answers)
      : true;
  });
}

export function isQuestionVisible(
  question: SurveyQuestion,
  answers: Record<string, string | string[]>,
): boolean {
  if (!question.DisplayLogic) {
    return true;
  }

  return evaluateDisplayLogic(question.DisplayLogic, answers);
}

export function hasLinkedFollowUpQuestion(
  questions: SurveyQuestion[],
  questionId: string,
): boolean {
  const questionLinkPrefix = `q://${questionId}/SelectableChoice/`;

  return questions.some(
    (question) =>
      question.QuestionID !== questionId &&
      JSON.stringify(question.DisplayLogic ?? {}).includes(questionLinkPrefix),
  );
}

export function isQuestionAnswered(
  question: SurveyQuestion,
  answer: string | string[] | undefined,
): boolean {
  if (Array.isArray(answer)) {
    return answer.length > 0;
  }

  return typeof answer === 'string' && answer.trim().length > 0;
}

export function canProceedQuestion(
  question: SurveyQuestion | null,
  answer: string | string[] | undefined,
  forceResponseOn: QualtricsForceResponse,
): boolean {
  if (!question) {
    return false;
  }

  const isForceResponseOn =
    question.Validation?.settings?.forceResponse === forceResponseOn;

  return isForceResponseOn ? isQuestionAnswered(question, answer) : true;
}

export function buildQualtricsSubmitPayload(params: {
  survey: QualtricsSurvey | null;
  surveyQuestions: SurveyQuestion[];
  answers: Record<string, string | string[]>;
  feedbackStartDate: string | null;
  portalLanguage: string;
  currentPage: string;
  triggerConfigurationId?: number;
}): QualtricsSurveySubmit | null {
  const {
    survey,
    surveyQuestions,
    answers,
    feedbackStartDate,
    portalLanguage,
    currentPage,
    triggerConfigurationId = 3,
  } = params;

  if (!survey) {
    return null;
  }

  const startDate = feedbackStartDate ?? new Date().toISOString();
  const endDate = new Date().toISOString();
  const values: Record<string, string | number> = {
    startDate,
    endDate,
    userLanguage: portalLanguage,
    currentPage,
  };

  surveyQuestions.forEach((question) => {
    const answer = answers[question.QuestionID];

    if (question.QuestionType === QUALTRICS_QUESTION_TYPES.TEXT_ENTRY) {
      values[`${question.QuestionID}_TEXT`] =
        typeof answer === 'string' ? answer : '';

      return;
    }

    if (Array.isArray(answer)) {
      values[question.QuestionID] = answer.join(',');

      return;
    }

    if (typeof answer === 'string' && answer.trim()) {
      const numericChoice = Number(answer);
      values[question.QuestionID] = Number.isNaN(numericChoice)
        ? answer
        : numericChoice;
    }
  });

  return {
    qualtricsmsg: { values },
    surveyDetails: {
      surveyId: survey.surveyId,
      qualtricsSurveyId: survey.qualtricsSurveyId,
      versionNumber: survey.versionNumber,
      responseId: crypto.randomUUID(),
      triggerConfigurationId,
    },
  };
}
